import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle, Download, FileText, Loader2, Mail, ShieldCheck, XCircle } from 'lucide-react';
import { signAPI } from '../services/api';
import { useBrandingStore } from '../context/brandingStore';
import SignatureCapture from '../components/common/SignatureCapture';
import { formatDateTime } from '../utils/format';

/**
 * Signing a contract by its emailed link (`/sign/:token`). No sign-in.
 *
 * Four steps, each recorded on the contract: confirm it is you (a six-digit
 * code to the address the sender named — skipped when you are signed in to
 * the portal with that verified address), read the document, agree to sign
 * electronically (the wording comes from the server and is stored exactly as
 * shown), then sign. The page sends back the fingerprint of the document it
 * showed; if the document changed meanwhile, nothing is signed.
 *
 * A contract may ask the signer to choose something as they sign (the term of
 * an order). The chooser picks before signing; anyone else sees the answer and
 * waits until it exists — the server refuses them otherwise.
 *
 * In a package (an MSA, then an Order) the page says "Document 1 of 2", and
 * once this one is signed offers to continue straight to the next: no second
 * code, but its own agreement to sign electronically.
 *
 * Customer-facing: names no vendor and no platform.
 */
export default function SignContractPage() {
  const { token } = useParams();
  const portalName = useBrandingStore((s) => s.branding?.portalName) || null;
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [agree, setAgree] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [signature, setSignature] = useState(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [picked, setPicked] = useState({});

  const { data, isLoading, error: loadError, refetch } = useQuery({
    queryKey: ['sign', token],
    queryFn: () => signAPI.get(token).then((r) => r.data),
    retry: false,
  });

  useEffect(() => {
    if (data?.signer?.name && !signedName) setSignedName(data.signer.name);
  }, [data, signedName]);

  // The document, as a blob URL so it opens without a second request to a
  // different origin. Reloaded only when the file itself changes — the signed
  // copy once the contract completes — not on every refresh of the page's data,
  // which left the viewer blank after each step.
  const docVersion = !data || data.state === 'voided' ? null : data.state === 'completed' ? 'signed' : 'original';
  useEffect(() => {
    if (!docVersion) return undefined;
    let url = null;
    let cancelled = false;
    signAPI.document(token).then((r) => {
      if (cancelled) return;
      url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      setPdfUrl(url);
    }).catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [token, docVersion]);

  const onSignature = useCallback((s) => setSignature(s), []);

  const act = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await refetch();
    } catch (e) {
      setError(e.response?.data?.error || 'Something went wrong. Please try again.');
      if (e.response?.data?.code === 'CHANGED') await refetch();
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    const r = await signAPI.document(token, true);
    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.contract.title} - signed.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const shell = (children) => (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 px-4 py-8">
      <div className="mx-auto w-full max-w-3xl space-y-5">
        {portalName && <div className="text-center text-sm font-medium text-neutral-500 dark:text-neutral-400">{portalName}</div>}
        {children}
      </div>
    </div>
  );
  const card = 'bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 space-y-4';
  const banner = error && (
    <div className="flex gap-2 rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-200">
      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
    </div>
  );

  if (isLoading) {
    return shell(<div className={`${card} flex items-center justify-center gap-2 text-neutral-500`}><Loader2 className="w-5 h-5 animate-spin" /> Opening the document…</div>);
  }
  if (loadError || !data) {
    return shell(
      <div className={`${card} text-center`}>
        <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
        <h1 className="text-lg font-semibold dark:text-white">This signing link is not valid</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">{loadError?.response?.data?.error || 'Use the latest email you were sent, or ask the sender for a new link.'}</p>
      </div>
    );
  }

  const { contract, signer, state } = data;
  const continueToNext = () => act(async () => {
    const r = await signAPI.next(token);
    window.location.assign(`/sign/${encodeURIComponent(r.data.token)}`);
  });
  const header = (
    <div className={card}>
      {data.package && (
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-primary-700 dark:text-primary-300">Document {data.package.position} of {data.package.total}</p>
          <ol className="flex flex-wrap gap-2 text-xs">
            {data.package.documents.map((d, i) => (
              <li key={i} className={`rounded px-2 py-0.5 ${d.current ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200' : ['completed', 'signed'].includes(d.status) ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'}`}>
                {i + 1}. {d.title}{['completed', 'signed'].includes(d.status) && !d.current ? ' ✓' : ''}
              </li>
            ))}
          </ol>
        </div>
      )}
      <div className="flex items-start gap-3">
        <FileText className="w-8 h-8 text-primary-600 flex-shrink-0" />
        <div className="min-w-0">
          <h1 className="text-xl font-semibold dark:text-white break-words">{contract.title}</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            {contract.companyName ? `For ${contract.companyName} · ` : ''}{contract.pageCount} page{contract.pageCount === 1 ? '' : 's'}
            {data.from?.name ? ` · from ${data.from.name}` : ''}
          </p>
        </div>
      </div>
      {contract.message && <p className="text-sm text-neutral-700 dark:text-neutral-200 whitespace-pre-line border-l-2 border-primary-300 pl-3">{contract.message}</p>}
      <ol className="flex flex-wrap gap-2 text-xs">
        {data.signers.filter((s) => s.role === 'signer').map((s) => (
          <li key={s.name} className={`rounded-full px-2.5 py-1 ${s.signed ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200'}`}>
            {s.signed ? '✓ ' : ''}{s.name}{s.you ? ' (you)' : ''}
          </li>
        ))}
      </ol>
    </div>
  );

  const documentView = pdfUrl && (
    <div className={card}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-medium dark:text-white">The document</h2>
        <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-sm text-primary-600 hover:underline">Open in a new tab</a>
      </div>
      <iframe title={contract.title} src={pdfUrl} className="w-full h-[70vh] rounded border border-neutral-200 dark:border-neutral-700 bg-white" />
    </div>
  );

  if (state === 'completed' || state === 'signed') {
    return shell(
      <>
        {header}
        <div className={`${card} text-center`}>
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
          <h2 className="text-lg font-semibold dark:text-white">{state === 'completed' ? 'Signed by everyone' : 'You have signed'}</h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            {state === 'completed'
              ? 'The signed copy, with a certificate of completion as its last page, has been emailed to everyone. You can also download it here.'
              : data.package && !data.next && data.package.documents.every((d) => d.current || ['signed', 'completed'].includes(d.status))
                ? `You have signed all ${data.package.total} documents. We will email you the signed copies once everyone has signed.`
                : 'We will email you the signed copy once everyone has signed.'}
          </p>
          {state === 'completed' && (
            <button onClick={download} className="btn-primary inline-flex items-center gap-2"><Download className="w-4 h-4" /> Download the signed copy</button>
          )}
          {banner}
          {data.next && (
            <button disabled={busy} onClick={continueToNext} className="btn-primary inline-flex items-center gap-2">
              {busy ? 'Opening…' : `Continue to document ${data.next.position} of ${data.package?.total || data.next.position}: ${data.next.title}`}
            </button>
          )}
        </div>
      </>
    );
  }

  if (['voided', 'declined', 'expired'].includes(state)) {
    const text = {
      voided: ['This document was withdrawn', 'The sender withdrew it, so it can no longer be signed.'],
      declined: ['This document was declined', 'It can no longer be signed. Contact the sender if that was a mistake.'],
      expired: ['This signing link has expired', 'Ask the sender to send it again.'],
    }[state];
    return shell(
      <>
        {header}
        <div className={`${card} text-center`}>
          <XCircle className="w-10 h-10 text-neutral-400 mx-auto" />
          <h2 className="text-lg font-semibold dark:text-white">{text[0]}</h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">{text[1]}{data.from?.supportEmail ? ` (${data.from.supportEmail})` : ''}</p>
        </div>
      </>
    );
  }

  const choices = data.choices || [];
  const mine = choices.filter((ch) => ch.yours);
  const choicesReady = mine.every((ch) => picked[ch.key]);

  if (data.waitingForChoices?.length && !mine.length && !['completed', 'signed', 'voided', 'declined', 'expired'].includes(state)) {
    const w = data.waitingForChoices[0];
    return shell(
      <>
        {header}
        <div className={`${card} text-center`}>
          <Loader2 className="w-8 h-8 text-neutral-400 mx-auto" />
          <h2 className="text-lg font-semibold dark:text-white">Not your turn yet</h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">{w.chooserName || 'Another signer'} chooses the {w.label.toLowerCase()} as they sign. Open this link again once they have.</p>
        </div>
        {documentView}
      </>
    );
  }

  if (data.waitingForOthers) {
    return shell(
      <>
        {header}
        <div className={`${card} text-center`}>
          <Loader2 className="w-8 h-8 text-neutral-400 mx-auto" />
          <h2 className="text-lg font-semibold dark:text-white">Not your turn yet</h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">Someone else signs before you. We will email you when it is your turn.</p>
        </div>
        {documentView}
      </>
    );
  }

  if (state === 'verify') {
    return shell(
      <>
        {header}
        <div className={card}>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary-600" />
            <h2 className="font-medium dark:text-white">First, confirm it is you</h2>
          </div>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            We will email a six-digit code to <strong>{signer.email}</strong>. It works for ten minutes.
          </p>
          {banner}
          {!codeSent && !signer.codeSentAt ? (
            <button disabled={busy} onClick={() => act(async () => { await signAPI.sendCode(token); setCodeSent(true); })} className="btn-primary">
              {busy ? 'Sending…' : 'Email me a code'}
            </button>
          ) : (
            <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); act(() => signAPI.verify(token, code)); }}>
              <label className="text-sm">
                <span className="block text-neutral-600 dark:text-neutral-300 mb-1">Code</span>
                <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code"
                  className="input w-36 tracking-widest text-lg" placeholder="000000" />
              </label>
              <button disabled={busy || code.length !== 6} className="btn-primary">{busy ? 'Checking…' : 'Confirm'}</button>
              <button type="button" disabled={busy} onClick={() => act(() => signAPI.sendCode(token))} className="text-sm text-primary-600 hover:underline">
                Send another code
              </button>
            </form>
          )}
        </div>
        {documentView}
      </>
    );
  }

  return shell(
    <>
      {header}
      {documentView}
      <div className={card}>
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary-600" />
          <h2 className="font-medium dark:text-white">{state === 'consent' ? 'Agree to sign electronically' : 'Sign'}</h2>
        </div>
        {banner}
        {state === 'consent' && (
          <>
            <p className="text-sm text-neutral-700 dark:text-neutral-200 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-3">{data.consentText}</p>
            <label className="flex items-start gap-2 text-sm dark:text-neutral-200">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" />
              I have read this and agree to sign electronically.
            </label>
            <button disabled={!agree || busy} onClick={() => act(() => signAPI.consent(token))} className="btn-primary">Continue</button>
          </>
        )}
        {state === 'sign' && (
          <>
            <label className="block text-sm">
              <span className="block text-neutral-600 dark:text-neutral-300 mb-1">Your full name</span>
              <input value={signedName} onChange={(e) => setSignedName(e.target.value)} className="input w-full max-w-md" />
            </label>
            {choices.length > 0 && (
              <div className="space-y-3">
                {choices.map((ch) => (ch.yours ? (
                  <fieldset key={ch.key} className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-3">
                    <legend className="px-1 text-sm font-medium dark:text-white">{ch.label}</legend>
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                      {ch.options.map((o) => (
                        <label key={o} className="inline-flex items-center gap-2 text-sm dark:text-neutral-200">
                          <input type="radio" name={`choice-${ch.key}`} value={o} checked={picked[ch.key] === o} onChange={() => setPicked({ ...picked, [ch.key]: o })} /> {o}
                        </label>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">Your choice is printed in the signed copy and cannot be changed after you sign.</p>
                  </fieldset>
                ) : (
                  <p key={ch.key} className="text-sm dark:text-neutral-200"><span className="text-neutral-500 dark:text-neutral-400">{ch.label}:</span> <strong>{ch.chosen}</strong>{ch.chosenBy ? ` (chosen by ${ch.chosenBy})` : ''}</p>
                )))}
              </div>
            )}
            <SignatureCapture onChange={onSignature} signerName={signer.name} />
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Signing as {signer.email}{{ portal_login: ' (signed in to the portal)', package: ' (continuing from the document you just signed)' }[signer.verifiedVia] || ' (confirmed by email code)'}. By selecting Sign you sign the document shown above, fingerprint {contract.sha256.slice(0, 12)}…
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                disabled={busy || !signature?.isValid || !signedName.trim() || !choicesReady}
                onClick={() => act(() => signAPI.sign(token, {
                  expectedSha256: contract.sha256, signatureType: signature.type, signatureData: signature.data, signedName: signedName.trim(),
                  ...(mine.length ? { choices: picked } : {}),
                }))}
                className="btn-primary"
              >
                {busy ? 'Signing…' : 'Sign'}
              </button>
              <button type="button" onClick={() => setDeclining(true)} className="btn-secondary">Decline</button>
            </div>
          </>
        )}
        {declining && (
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4 space-y-3">
            <p className="text-sm dark:text-neutral-200">Declining closes this document for everyone. The sender is told, with your reason.</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="input w-full" placeholder="Reason (optional)" />
            <div className="flex gap-3">
              <button disabled={busy} onClick={() => act(() => signAPI.decline(token, reason))} className="btn-danger">Decline the document</button>
              <button onClick={() => setDeclining(false)} className="btn-secondary">Keep it</button>
            </div>
          </div>
        )}
        {contract.expiresAt && <p className="text-xs text-neutral-500 dark:text-neutral-400">This link works until {formatDateTime(contract.expiresAt)}.</p>}
      </div>
    </>
  );
}
